import React, { useState } from 'react';
import { httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '@/firebase/config';
import { getFunctions } from 'firebase/functions';

/**
 * CSV Upload Utility for Admin Panel - UPDATED with Hand Column Support
 * 
 * Provides interface for uploading and processing word training CSV files
 * with explicit hand specification using Firebase Cloud Functions.
 */

interface CSVUploadUtilityProps {
  onUploadComplete?: (result: any) => void;
  onUploadError?: (error: string) => void;
}

// 🔥 NEW: Enhanced result interface
interface UploadResult {
  success: boolean;
  processed: number;
  successful: number;
  failed: number;
  skipped?: number;          // 🔥 NEW: Skipped rows
  errors: string[];
  handDistribution?: {        // 🔥 NEW: Hand statistics
    left: number;
    right: number;
  };
  message: string;
}

const CSVUploadUtility: React.FC<CSVUploadUtilityProps> = ({
  onUploadComplete,
  onUploadError
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  
  // Initialize Firebase Functions with the correct region
  const functions = (() => {
    const funcs = getFunctions(app, 'us-central1'); // Specify the region where functions are deployed
    
    // Connect to emulator in development if needed
    if (import.meta.env.DEV && !import.meta.env.VITE_USE_FIREBASE_EMULATOR_DISABLED) {
      try {
        // Check if already connected to avoid re-connection errors
        const isConnected = (funcs as any)._delegate?.region?.includes('localhost');
        if (!isConnected) {
          connectFunctionsEmulator(funcs, 'localhost', 5001);
          console.log('🔧 Connected to Firebase Functions emulator');
        }
      } catch (error) {
        console.log('ℹ️ Functions emulator connection skipped:', error instanceof Error ? error.message : 'Unknown error');
      }
    }
    
    return funcs;
  })();
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      
      setFile(selectedFile);
      setError(null);
      setResult(null);
      setValidationResult(null);
    }
  };
  
  const validateCSV = async () => {
    if (!file) return;
    
    setValidating(true);
    setError(null);
    setProgress('Reading file...');
    
    try {
      const csvContent = await file.text();
      setProgress('Validating format...');
      
      const validateFunction = httpsCallable(functions, 'validateWordTrainingCSV');
      const result = await validateFunction({ csvContent });
      
      const validation = result.data as any;
      setValidationResult(validation);

      if (validation.valid) {
        setProgress(
          `✅ Validation passed! Found ${validation.previewRows} rows to process. ` +
          `Hand distribution: ${validation.handDistribution?.left || 0} left, ${validation.handDistribution?.right || 0} right`
        );
        console.log('✅ CSV validation successful:', validation);
      } else {
        setError(`Validation failed: ${validation.errors.join(', ')}`);
        setProgress('');
      }
    } catch (error) {
      console.error('❌ CSV validation error:', error);
      setError(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setProgress('');
    } finally {
      setValidating(false);
    }
  };
  
  const uploadCSV = async () => {
    if (!file) return;
    
    setUploading(true);
    setError(null);
    setResult(null);
    setProgress('Reading file...');
    
    try {
      const csvContent = await file.text();
      setProgress('Uploading to server...');
      
      const parseFunction = httpsCallable(functions, 'parseWordTrainingCSV');
      const result = await parseFunction({ 
        csvContent, 
        fileName: file.name 
      });
      
      const uploadResult = result.data as UploadResult;
      
      if (uploadResult.success) {
        setResult(uploadResult);
        setProgress(
          `✅ Upload completed! ${uploadResult.successful} finger combinations processed. ` +
          `Hand distribution: ${uploadResult.handDistribution?.left || 0} left, ${uploadResult.handDistribution?.right || 0} right`
        );
        console.log('✅ CSV upload successful:', uploadResult);
        
        if (onUploadComplete) {
          onUploadComplete(uploadResult);
        }
      } else {
        setError(`Upload failed: ${uploadResult.message || 'Unknown error'}`);
        setProgress('');
        
        if (onUploadError) {
          onUploadError(uploadResult.message || 'Upload failed');
        }
      }
    } catch (error) {
      console.error('❌ CSV upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Upload failed: ${errorMessage}`);
      setProgress('');
      
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };
  
  // 🔥 UPDATED: Sample CSV with Hand column and real examples
  const downloadSample = () => {
    const sampleCSV = `FingerCombination,Hand,3-letters,4-letters,5-letters,6-letters,7-letters,8-letters,Phrases
T_R_P,Left,awe|axe|saw|sax|sea|see|sew|sex|was|wax|wee|zee,axes|ease|eave|saws|seas|sees|zees,eases|eaves|sexes|waxes,seesaw,seesaws,,A wee zee saw a wax axe|Sex eases a sax|We see awe|A seesaw eases the zees|Sew the seas|Wax the eaves|We saw wax sexes on the seesaws|A sax was in awe of the sea
T_R_P,Right,imp|ilk|kip|koi|lip|lop|mil|mop|oil|pip|poi|pol|pop,kilo|kill|limo|limp|lilo|milk|moki|moil|plop|polk|poll|polo|pomp,polio,pompom,,lollipop,A pompom with polio|Kill the moki with a lollipop|A kilo of milk plop in the limo|I mop oil with a pompom|The imp on the lilo has a limp|The polo imp has a lollipop|A poll to kill the imp|Mop up the oil|Pop the pompom|A kilo of pomp for the poll
P_R,Left,sap|asp|spa|are|ear|era,wasp|paws|sear|rear|gear,swaps|gears|spear|spare,grapes|grasp|spark,sparks|grasps|sprayer,sprayers|grasping|sparking,A wasp wasp|Swap paws fast|A rare gear spark|Spare grapes are great|Grasp sparks with care
P_R,Right,,,,,,,Cannot form real words - only consonants available. Please select different fingers or switch to letter training mode.`;
    
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_word_training_with_hands.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const resetUpload = () => {
    setFile(null);
    setUploading(false);
    setValidating(false);
    setProgress('');
    setResult(null);
    setError(null);
    setValidationResult(null);
  };
  
  return (
    <div className="csv-upload-utility">
      <div className="upload-header">
        <h3>📤 CSV Upload Utility</h3>
        <p className="upload-description">
          Upload a CSV file containing finger combination to word mappings with explicit hand specification.
          <br />
          <strong>Required format:</strong> FingerCombination, Hand, 3-letters, 4-letters, 5-letters, 6-letters, 7-letters, 8-letters, Phrases
        </p>
      </div>
      
      {/* File Selection */}
      <div className="file-selection">
        <div className="file-input-wrapper">
          <input 
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="file-input"
            id="csv-file-input"
            disabled={uploading || validating}
          />
          <label htmlFor="csv-file-input" className="file-input-label">
            {file ? file.name : '📁 Choose CSV File'}
          </label>
        </div>
        
        <button 
          className="sample-btn"
          onClick={downloadSample}
          disabled={uploading || validating}
        >
          📥 Download Sample (with Hand Column)
        </button>
      </div>
      
      {/* Progress Display */}
      {progress && (
        <div className="progress-display">
          <div className="progress-text">{progress}</div>
          {(uploading || validating) && (
            <div className="progress-spinner"></div>
          )}
        </div>
      )}
      
      {/* Error Display */}
      {error && (
        <div className="error-display">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
        </div>
      )}

      {/* 🔥 NEW: Validation Result Display */}
      {validationResult && !validationResult.valid && (
        <div className="validation-display">
          <div className="validation-header">🔍 Validation Details</div>
          
          {validationResult.expectedHeaders && (
            <div className="validation-section">
              <div className="section-title">Expected Headers:</div>
              <div className="header-list">
                {validationResult.expectedHeaders.map((header: string, index: number) => (
                  <span key={index} className="header-tag expected">{header}</span>
                ))}
              </div>
            </div>
          )}
          
          {validationResult.actualHeaders && (
            <div className="validation-section">
              <div className="section-title">Found Headers:</div>
              <div className="header-list">
                {validationResult.actualHeaders.map((header: string, index: number) => (
                  <span key={index} className={`header-tag ${validationResult.expectedHeaders?.includes(header) ? 'valid' : 'invalid'}`}>
                    {header}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {validationResult.sampleData && (
            <div className="validation-section">
              <div className="section-title">Sample Data (first 3 rows):</div>
              <div className="sample-data">
                {validationResult.sampleData.map((row: any, index: number) => (
                  <div key={index} className="sample-row">
                    Row {index + 1}: {JSON.stringify(row, null, 2)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Result Display */}
      {result && (
        <div className="result-display">
          <div className="result-header">✅ Upload Results</div>
          <div className="result-stats">
            <div className="stat">Processed: {result.processed} rows</div>
            <div className="stat">Successful: {result.successful} combinations</div>
            <div className="stat">Failed: {result.failed} combinations</div>
            {result.skipped !== undefined && result.skipped > 0 && (
              <div className="stat">Skipped: {result.skipped} combinations (no words available)</div>
            )}
            {/* 🔥 NEW: Hand distribution display */}
            {result.handDistribution && (
              <>
                <div className="stat">Left Hand: {result.handDistribution.left} combinations</div>
                <div className="stat">Right Hand: {result.handDistribution.right} combinations</div>
              </>
            )}
          </div>
          
          {result.errors && result.errors.length > 0 && (
            <div className="result-errors">
              <div className="errors-header">Errors:</div>
              <div className="errors-list">
                {result.errors.slice(0, 5).map((error: string, index: number) => (
                  <div key={index} className="error-item">{error}</div>
                ))}
                {result.errors.length > 5 && (
                  <div className="error-item">... and {result.errors.length - 5} more errors</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="action-buttons">
        {file && !result && (
          <>
            <button 
              className="action-btn validate-btn"
              onClick={validateCSV}
              disabled={uploading || validating}
            >
              {validating ? (
                <>
                  <div className="btn-spinner"></div>
                  Validating...
                </>
              ) : (
                '🔍 Validate Format'
              )}
            </button>
            
            <button 
              className="action-btn upload-btn"
              onClick={uploadCSV}
              disabled={uploading || validating}
            >
              {uploading ? (
                <>
                  <div className="btn-spinner"></div>
                  Uploading...
                </>
              ) : (
                '📤 Upload & Process'
              )}
            </button>
          </>
        )}
        
        {(file || result || error) && (
          <button 
            className="action-btn reset-btn"
            onClick={resetUpload}
            disabled={uploading || validating}
          >
            🔄 Reset
          </button>
        )}
      </div>
      
      <style>{`
        .csv-upload-utility {
          background: rgba(31, 41, 55, 0.3);
          border-radius: 8px;
          padding: 20px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          margin-top: 20px;
        }
        
        .upload-header {
          margin-bottom: 20px;
        }
        
        .upload-header h3 {
          margin: 0 0 8px 0;
          color: #3b82f6;
          font-size: 1.1rem;
        }
        
        .upload-description {
          margin: 0;
          color: #9ca3af;
          font-size: 0.85rem;
          line-height: 1.4;
        }
        
        .file-selection {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .file-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
        }
        
        .file-input {
          display: none;
        }
        
        .file-input-label {
          display: block;
          padding: 10px 16px;
          background: rgba(59, 130, 246, 0.2);
          border: 2px dashed rgba(59, 130, 246, 0.5);
          border-radius: 8px;
          color: #60a5fa;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }
        
        .file-input-label:hover {
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.7);
          color: white;
        }
        
        .sample-btn {
          padding: 10px 16px;
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.5);
          border-radius: 6px;
          color: #22c55e;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        
        .sample-btn:hover:not(:disabled) {
          background: rgba(34, 197, 94, 0.3);
          color: white;
        }
        
        .sample-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .progress-display {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 6px;
          margin-bottom: 16px;
        }
        
        .progress-text {
          color: #60a5fa;
          font-size: 0.9rem;
          line-height: 1.4;
        }
        
        .progress-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .error-display {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          margin-bottom: 16px;
        }
        
        .error-text {
          color: #fca5a5;
          font-size: 0.9rem;
        }

        /* 🔥 NEW: Validation result styles */
        .validation-display {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 16px;
        }
        
        .validation-header {
          color: #f59e0b;
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 12px;
        }
        
        .validation-section {
          margin-bottom: 12px;
        }
        
        .section-title {
          color: #fbbf24;
          font-weight: 500;
          font-size: 0.9rem;
          margin-bottom: 6px;
        }
        
        .header-list {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        
        .header-tag {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
          font-family: monospace;
          font-weight: 500;
        }
        
        .header-tag.expected {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.5);
          color: #60a5fa;
        }
        
        .header-tag.valid {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.5);
          color: #22c55e;
        }
        
        .header-tag.invalid {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.5);
          color: #ef4444;
        }
        
        .sample-data {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
          padding: 8px;
          max-height: 200px;
          overflow-y: auto;
        }
        
        .sample-row {
          color: #d1d5db;
          font-size: 0.75rem;
          font-family: monospace;
          margin-bottom: 8px;
          white-space: pre-wrap;
        }
        
        .result-display {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 16px;
        }
        
        .result-header {
          color: #22c55e;
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 12px;
        }
        
        .result-stats {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        
        .stat {
          color: #86efac;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .result-errors {
          border-top: 1px solid rgba(34, 197, 94, 0.3);
          padding-top: 12px;
        }
        
        .errors-header {
          color: #fbbf24;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 8px;
        }
        
        .errors-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .error-item {
          color: #fed7aa;
          font-size: 0.8rem;
          font-family: monospace;
        }
        
        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .action-btn {
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        
        .validate-btn {
          background: linear-gradient(45deg, #8b5cf6, #7c3aed);
          color: white;
        }
        
        .upload-btn {
          background: linear-gradient(45deg, #22c55e, #16a34a);
          color: white;
        }
        
        .reset-btn {
          background: linear-gradient(45deg, #6b7280, #4b5563);
          color: white;
        }
        
        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        
        .btn-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .file-selection {
            flex-direction: column;
          }
          
          .file-input-wrapper {
            width: 100%;
          }
          
          .result-stats {
            flex-direction: column;
            gap: 8px;
          }
          
          .action-buttons {
            flex-direction: column;
          }

          .header-list {
            flex-direction: column;
            gap: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default CSVUploadUtility;
